import React, { useState, useEffect } from 'react';
import { Line, Pie } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  ArcElement,
  Title, 
  Tooltip, 
  Legend 
} from 'chart.js';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import IconButton from '@mui/material/IconButton';
import { Typography, Button } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import './Dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('primaryColor') || '#0f0f0f');
  const [secondaryColor, setSecondaryColor] = useState(localStorage.getItem('secondaryColor') || '#1a1a1a');
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');

  const [trafficSummaries, setTrafficSummaries] = useState({});

  const [totalTraffic, setTotalTraffic] = useState(34245);
  const [todayTraffic, setTodayTraffic] = useState(1632);
  const [totalSpeeding, setTotalSpeeding] = useState(75);
  const [todaySpeeding, setTodaySpeeding] = useState(31);
  const [error, setError] = useState(null);
  
  const [timeRange, setTimeRange] = useState('daily');
  const [selectedDevice, setSelectedDevice] = useState('all');
  const [chartData, setChartData] = useState([]);

  const [schedules, setSchedules] = useState([]);
  const [operatingStatus, setOperatingStatus] = useState([]);

  useEffect(() => {
    fetchRecordSummaries();
    fetchSchedules();
    fetchOperatingStatus();
  }, []);

  const fetchRecordSummaries = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/traffic/record-summaries`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setTrafficSummaries(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching presets:', error);
      setError('Failed to fetch presets. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchChart = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/traffic/chart-data?timeRange=${timeRange}&device=${selectedDevice}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setChartData(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching traffic summaries:', error);
      setError('Failed to fetch traffic summaries. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedules = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/traffic/upcoming-schedules`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSchedules(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching traffic summaries:', error);
      setError('Failed to fetch traffic summaries. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchOperatingStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/traffic/operating-status`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setOperatingStatus(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching traffic summaries:', error);
      setError('Failed to fetch traffic summaries. Please try again later.');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchChart();
  }, [timeRange, selectedDevice]);


  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'primaryColor') {
        setPrimaryColor(event.newValue || '#0f0f0f');
      } else if (event.key === 'secondaryColor') {
        setSecondaryColor(event.newValue || '#1a1a1a');
      } else if (event.key === 'darkMode') {
        setDarkMode(event.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const handleColorChange = () => {
      setPrimaryColor(localStorage.getItem('primaryColor') || '#0f0f0f');
      setSecondaryColor(localStorage.getItem('secondaryColor') || '#1a1a1a');
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    };

    const intervalId = setInterval(handleColorChange, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, []);

  const CardSkeleton = () => (
    <div className="dashboard-card skeleton-card" style={{ backgroundColor: secondaryColor }}>
      <div className="dashboard-card-main">
        <div className="skeleton-title"></div>
        <div className="skeleton-value"></div>
        <div className="skeleton-text"></div>
      </div>
    </div>
  );

  const ChartSkeleton = () => (
    <div className="dashboard-card skeleton-chart" style={{ backgroundColor: secondaryColor }}>
      <div className="skeleton-title"></div>
      <div className="skeleton-chart-area"></div>
    </div>
  );

  const TableSkeleton = () => (
    <div className="dashboard-card skeleton-table" style={{ backgroundColor: secondaryColor }}>
      <div className="skeleton-title"></div>
      <div className="skeleton-rows">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="skeleton-row"></div>
        ))}
      </div>
    </div>
  );

  const getChartData = () => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    
    return {
      labels: timeRange === 'daily' 
          ? Array.from({length: 24}, (_, i) => `${i.toString().padStart(2, '0')}:00`)
          : Array.from({length: timeRange === 'weekly' ? 7 : 30}, (_, i) => {
              const date = new Date(yesterday);
              date.setDate(yesterday.getDate() - i);
              return timeRange === 'weekly'
                  ? date.toLocaleDateString('en-US', { weekday: 'short' })
                  : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          }).reverse(),
      datasets: [
          {
              label: 'Last 7 Days Traffic',
              data: chartData.map(item => item.total),
              borderColor: '#4CAF50',
              backgroundColor: 'rgba(76, 175, 80, 0.3)',
              tension: 0.4,
              fill: {
                  target: 'origin',
                  above: 'rgba(76, 175, 80, 0.3)'
              }
          },
          {
              label: 'Speeding',
              data: chartData.map(item => item.speeding),
              borderColor: '#F44336',
              backgroundColor: 'rgba(244, 67, 54, 0.3)',
              tension: 0.4,
              fill: {
                  target: '-1',
                  above: 'rgba(244, 67, 54, 0.3)'
              }
          }
      ]
    };
  };
  
  const trafficChartData = {
    labels: getChartData().labels,
    datasets: getChartData().datasets
  };
  /*
  const trafficChartData = {
    labels: getChartData().labels,
    datasets: [{
      label: 'Traffic',
      data: getChartData().data,
      borderColor: '#4CAF50',
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
      hoverBackgroundColor: 'rgba(76, 175, 80, 0.3)',
      fill: true,
      tension: 0.4,
      pointRadius: 3,
      pointHoverRadius: 10,
      segment: {
        borderColor: '#4CAF50',
      }
    }]
  };
  */
  
  const trafficChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: getChartData().title,
        color: darkMode ? '#CCCCCC' : '#666666',
        font: { size: 14 }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
        titleColor: darkMode ? '#FFFFFF' : '#000000',
        bodyColor: darkMode ? '#FFFFFF' : '#000000',
        borderColor: '#4CAF50',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            return `Records: ${context?.parsed?.y?.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: darkMode ? '#CCCCCC' : '#666666' }
      },
      y: {
        grid: { color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' },
        ticks: { color: darkMode ? '#CCCCCC' : '#666666' }
      }
    }
  };

  const screenStatusOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: () => {
      navigate('/overview');
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          color: darkMode ? '#FFFFFF' : '#000000',
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12
          }
        },
        onClick: () => null
      }
    },
    cutout: '50%',
    radius: '90%',
    hoverOffset: 10
  };
  
  const screenStatusData = {
    labels: ['Online', 'Power Saving', 'Low Signal Strength', 'Offline'],
    datasets: [{
      data: [
        operatingStatus.running,
        operatingStatus.powerSaving,
        operatingStatus.offline,
        0
      ],

      backgroundColor: [
        '#4CAF50', '#cccccc', '#ffa500', '#000000'
      ],
      borderWidth: 0,
      cutout: '70%',
      radius: '90%'
    }]
  };
  

  const DashboardCard = ({ title, total, speeding }) => (
    <div className="dashboard-card" style={{ backgroundColor: secondaryColor }}>
      <div style={{ backgroundColor: secondaryColor, padding: '20px', borderRadius: '8px' }}>
        <div style={{ marginBottom: '20px' }}>
          <Typography variant="h8">{title} Traffic Records</Typography>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div>
              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Total</Typography>
              <Typography variant="h9">{total?.toLocaleString()}</Typography>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', color: '#F44336' }}>
            <div>
              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Speeding</Typography>
              <Typography variant="h9">{speeding?.toLocaleString()}</Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`dashboard-container ${darkMode ? 'dark-mode' : 'light-mode'}`} style={{ backgroundColor: primaryColor }}>
      <div className="dashboard-row">
        <div className="dashboard-column dashboard-card-container">
          {loading ? (
            <>
              <CardSkeleton />
              <CardSkeleton />
            </>
          ) : (
            <>
              <DashboardCard title="Last 7 Days" total={trafficSummaries.totalTrafficRecords} speeding={trafficSummaries.totalSpeedingRecords}/>
              <DashboardCard title="Yesterday" total={trafficSummaries.yesterdayTotalTrafficRecords} speeding={trafficSummaries.yesterdayTotalSpeedingRecords}/>
            </>
          )}
        </div>
        <div className="dashboard-column dashboard-chart-container">
          {loading ? <ChartSkeleton /> : <div className="dashboard-column dashboard-chart-container">
          <div className="dashboard-chart" style={{ backgroundColor: secondaryColor }}>
            <div className="chart-header">
              <h3 className="dashboard-card-title">Traffic Records</h3>
              
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                size="small"
                sx={{
                  minWidth: 120,
                  color: 'inherit',
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(76, 175, 80, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(76, 175, 80, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4CAF50',
                  },
                  '.MuiSelect-icon': {
                    color: 'inherit'
                  }
                }}
              >
                <MenuItem value="daily">Yesterday</MenuItem>
                <MenuItem value="weekly">Last 7 Days</MenuItem>
                <MenuItem value="monthly">Last 30 Days</MenuItem>
              </Select>
            </div>
            <div className="chart-container">
              <Line options={trafficChartOptions} data={trafficChartData} id="trafficChart" />
            </div>
          </div>
        </div>}
        </div>
      </div>

      <div className="dashboard-row">
        {loading ? (
          <>
            <TableSkeleton />
            <ChartSkeleton />
          </>
        ) : (
          <>
            <div className="dashboard-card" style={{ backgroundColor: secondaryColor }}>
        <div className="dashboard-card-header">
          <h3 className="dashboard-card-title">Upcoming Schedules</h3>
        </div>
          <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Devices</th>
                <th>Preset</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {schedules.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', color: '#4CAF50', fontWeight: 'bold' }}>
                    No schedule
                  </td>
                </tr>
              ) : (
                schedules.map((schedule) => (
                  <tr key={schedule.schedule.id}>
                    <td>{schedule.date}</td>
                    <td>{schedule.time}</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {schedule.devices.names.slice(0, 2).map((name, index) => (
                          <span 
                            key={index} 
                            className="table-link"
                            onClick={() => navigate(`/device/${schedule.devices.ids[index]}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            {name}
                          </span>
                        ))}
                        {schedule.devices.names.length > 2 && (
                          <>+ {schedule.devices.names.length - 2} more</>
                        )}
                      </div>
                    </td>
                    <td>
                      <span 
                        className="table-link"
                        onClick={() => navigate(`/presets/${schedule.preset.id}`)}
                      >
                        {schedule.preset.name}
                      </span>
                    </td>
                    <td className="action-cell">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/schedules`)}
                        sx={{ 
                          color: 'inherit',
                          '&:hover': { color: '#4CAF50' }
                        }}
                      >
                        <ArrowForwardIcon fontSize="small" />
                      </IconButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
        <div className="dashboard-card" style={{ backgroundColor: secondaryColor }}>
          <div className="dashboard-card-header">
            <h3 className="dashboard-card-title">Operating Status</h3>
          </div>
          <div className="chart-container" style={{ height: '1000px' }}>
            <Pie 
              data={screenStatusData} 
              options={screenStatusOptions}
            />
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;